import { from, Observable, of, zip } from 'rxjs';
import { Injectable } from '@angular/core';
import { NumberHash } from '@app/shared/utils/utils-types';
import { ConfigService, ConfigServiceModel, FormType } from '@app/origami-api/services/config.service';
import { map, take, tap } from 'rxjs/operators';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { localizeFieldsTitles } from '@app/shared/forms/dynamic-form/dynamic-form.utils';
import { Field } from '@app/origami-api/constants/field-flag';
import { Router } from '@angular/router';
import { cloneDeep } from 'lodash';

@Injectable()
export class DefaultFormsService {
  private formsByTopicType: NumberHash<NumberHash<ConfigServiceModel.Form>> = {};
  private loadersByTopicTypeInProgress: NumberHash<NumberHash<Promise<NumberHash<ConfigServiceModel.Form>>>> = {};
  public readonly domainExportForm: NumberHash<Observable<ConfigServiceModel.Form>> = {};

  constructor(
    private readonly configService: ConfigService,
    private readonly router: Router,
  ) {}

  getFormById(
    roomId: number,
    topicTypeId: number,
    isLinkedRoomMetadataMode?: boolean,
    force = false,
  ): Observable<ConfigServiceModel.Form> {
    return this.getFormsById(roomId, [topicTypeId], isLinkedRoomMetadataMode, force).pipe(
      map((hash) => hash[topicTypeId]),
    );
  }

  getFormsById(
    roomId: number,
    topicTypeIds: number[],
    isLinkedRoomMetadataMode?: boolean,
    force = false,
  ): Observable<NumberHash<ConfigServiceModel.Form>> {
    // we have to handle a case when forms are loaded from the linked eTMF room (while working with TMF documents in CWS).
    // let's cache such form with negative key
    roomId = isLinkedRoomMetadataMode ? -roomId : roomId;

    this.formsByTopicType[roomId] = this.formsByTopicType[roomId] || {};
    const hash = this.formsByTopicType[roomId];
    const notLoadedTopicTypeIds = topicTypeIds.filter((topicTypeId) => !hash[topicTypeId]);

    if (!notLoadedTopicTypeIds.length && !force) {
      return of(this.getFormsFromHash(roomId, topicTypeIds)).pipe(
        map((hash) => {
          return this.filterSsuSpecificFields(hash);
        }),
      );
    }

    const topicTypeIdsInProgress = this.getTopicTypeIdsInProgress(roomId);

    if (!topicTypeIdsInProgress.length || force) {
      return from(this.loadForms(roomId, topicTypeIds, isLinkedRoomMetadataMode, force)).pipe(
        map((hash) => {
          return this.filterSsuSpecificFields(hash);
        }),
      );
    }

    const newTopicTypeIds = [];
    let loaders = [];

    notLoadedTopicTypeIds.forEach((id) => {
      const loader = this.loadersByTopicTypeInProgress[roomId][id];

      if (!loader) {
        newTopicTypeIds.push(+id);
      } else if (loaders.indexOf(loader) === -1) {
        loaders.push(loader);
      }
    });

    loaders = loaders.map((loader) => from(loader));
    if (newTopicTypeIds.length) {
      loaders.push(from(this.loadForms(roomId, newTopicTypeIds, isLinkedRoomMetadataMode)));
    }

    return zip(...loaders).pipe(map(() => this.getFormsFromHash(roomId, topicTypeIds)));
  }

  clear(roomId: number) {
    this.formsByTopicType[roomId] = {};
    this.loadersByTopicTypeInProgress[roomId] = {};
  }

  private getTopicTypeIdsInProgress(roomId: number): number[] {
    const hash = this.loadersByTopicTypeInProgress[roomId];

    if (hash) {
      return Object.keys(hash)
        .filter((id) => hash[id])
        .map((id) => +id);
    }
    return [];
  }

  private loadForms(
    roomId: number,
    topicTypeIds: number[],
    isLinkedRoomMetadataMode?: boolean,
    force = false,
  ): Promise<NumberHash<ConfigServiceModel.Form>> {
    const newLoader = new Promise<NumberHash<ConfigServiceModel.Form>>((resolve) => {
      this.configService
        .getDefaultForms(
          {
            siteId: Math.abs(roomId),
            topicTypeIds: topicTypeIds,
            tmfPublishing: {
              RetrieveFromTmf: isLinkedRoomMetadataMode,
            },
          },
          [ErrorCode.USER_DOES_NOT_HAVE_ACCESS_TO_ROOM],
          force,
        )
        .subscribe((resp) => {
          const formsByTopicType = resp.Success && resp.Result ? resp.Result : [];

          formsByTopicType.forEach((form) => {
            this.formsByTopicType[roomId][form.TopicTypeId] = localizeFieldsTitles(form);
          });
          topicTypeIds.forEach((id) => (this.loadersByTopicTypeInProgress[roomId][id] = null));

          resolve(this.formsByTopicType[roomId]);
        });
    });

    this.loadersByTopicTypeInProgress[roomId] = this.loadersByTopicTypeInProgress[roomId] || {};
    topicTypeIds.forEach((id) => (this.loadersByTopicTypeInProgress[roomId][id] = newLoader));

    return newLoader;
  }

  private getFormsFromHash(roomId: number, topicTypeIds: number[]): NumberHash<ConfigServiceModel.Form> {
    const res = {};
    topicTypeIds.forEach((topicTypeId) => (res[topicTypeId] = this.formsByTopicType[roomId][topicTypeId]));
    return res;
  }

  private filterSsuSpecificFields(hash: NumberHash<ConfigServiceModel.Form>): NumberHash<ConfigServiceModel.Form> {
    const isSSU = this.router.url.indexOf('/startup') !== -1;

    if (isSSU) {
      return hash;
    }

    const updatedHash: NumberHash<ConfigServiceModel.Form> = [];

    // TODO: more efficient proposal to check
    /*
    return Object.fromEntries(
      Object.entries(hash).map(([roomId, formValue]) => [
        roomId,
        {
          ...formValue,
          Fields: formValue.Fields.filter((field) => !Field.Is.ssuSpecific(field)),
        },
      ]),
    );
    */

    Object.keys(hash).forEach((roomId) => {
      updatedHash[roomId] = cloneDeep(hash[roomId]); // possible performance issue
      updatedHash[roomId].Fields = updatedHash[roomId].Fields.filter((field) => {
        return !Field.Is.ssuSpecific(field);
      });
    });

    return updatedHash;
  }

  getCustomFieldsForm(
    formId: number,
    formType: FormType,
    roomId: number,
    topicTypeId?: number,
    options?: unknown,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<ConfigServiceModel.Form> {
    return this.configService
      .getCustomFieldsForm(
        {
          formId: formId,
          formTypeId: formType,
          siteId: roomId,
          topicTypeId: topicTypeId,
        },
        options,
        silentErrorCodes,
        forceRequest,
      )
      .pipe(
        take(1),
        map((res) => res.Result),
        tap((form) => localizeFieldsTitles(form)),
      );
  }

  setFormByTopicId(roomId: number, topicId: number, form): void {
    this.formsByTopicType[roomId] = this.formsByTopicType[roomId] || {};
    this.formsByTopicType[roomId][topicId] = form;
  }
}
